
import { type ClientPlugin, type AppSystemClient } from '@source-digital/sd-app-client-core'
import { type ExperienceApp } from '@source-digital/sd-app-plugin-experience-app'

async function getCampaignPrefetched (eeApp: ExperienceApp) {
  if ('isCampaignPrefetched' in eeApp) {
    return (await eeApp.isCampaignPrefetched())
  }
  // TODO remove this when isCampaignPrefetched is available
  if ('getExperienceIndexData' in eeApp) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment, @typescript-eslint/prefer-ts-expect-error
    // @ts-ignore ignore this error
    const experienceIndexData = await eeApp?.getExperienceIndexData?.()
    return experienceIndexData?.build?.mode !== 'static'
  }
  return false
}

export interface CampaignPluginConfig {
  disabled?: boolean
}

function campaignPlugin (config: CampaignPluginConfig): ClientPlugin {
  return {
    id: '@source-digital/sd-app-client-plugin-campaign',
    async install (client: AppSystemClient, config?: CampaignPluginConfig) {
      const logger = client.logger.createLogger('client-plugin-campaign')
      logger.log('install started', config)
      if (config?.disabled === true) {
        return
      }
      const { api: ExperienceApp, events: ExperienceAppEvents } = await client.connect('@source-digital/sd-app-plugin-experience-app')
      const { api: ExperienceApi } = await client.connect('@source-digital/sd-app-plugin-experience-api')
      const isCampaignPrefetched = await getCampaignPrefetched(ExperienceApp)

      // if build mode is not static, do not run this plugin
      if (isCampaignPrefetched) {
        logger.log('Campaign is prefetched. Skipping campaign plugin.')
        return
      }

      ExperienceAppEvents.on('boot', async () => {
      // hide activations until hydrated
      // TODO: this causes issues when multiple plugins are installed. Need to find a better way to handle this.
        await ExperienceApp.hideActivations()
      })

      async function onHydrated () {
        const distribution = await ExperienceApp.getDistribution()
        if (distribution == null) {
          logger.log('No distribution found.')
          return
        }
        logger.log('distribution received', distribution)
        // get campaign activations
        const { data: campaignActivationsResponse } = await ExperienceApi.experience.getCampaignActivations({ params: { distributionId: distribution.id as string } })
        if (campaignActivationsResponse == null) {
          logger.log('Server response is empty.')
          return
        }

        if (campaignActivationsResponse.disableDistributionActivations === true) {
          await ExperienceApp.replaceAllActivations(campaignActivationsResponse.items)
          return
        }

        // get current activations to merge and filter
        const currentActivations = await ExperienceApp.getActivations()
        const accessControlRules = [
          ...(distribution.settings?.accessControlRules ?? []),
          ...(campaignActivationsResponse.accessControlRules ?? [])
        ]

        // if there's no access control rules, no need to filter existing activations
        if (currentActivations == null || currentActivations.length === 0 || accessControlRules.length === 0) {
          await ExperienceApp.pushActivations(campaignActivationsResponse.items)
          return
        }

        const { data: filteredActivations } = await ExperienceApi.activations.applyAccessControls({ data: { items: currentActivations, accessControlRules } })
        await ExperienceApp.replaceAllActivations([
          ...filteredActivations,
          ...(campaignActivationsResponse.items ?? [])
        ])
      }

      ExperienceAppEvents.on('hydrated', async () => {
        await onHydrated()
        await ExperienceApp.showActivations()
      })
    }
  }
}

export default campaignPlugin
